/**
 * "Remember me for 30 days" (docs/05 session handling). Firebase Auth already persists the session
 * locally; what forces daily re-login on this desk tool is the idle auto-lock in AuthProvider. This
 * per-browser, opt-in flag suspends that lock for a fixed window, after which the session is signed
 * out regardless of activity. The flag never outlives a sign-out (signOutEverywhere clears it), so a
 * shared machine is never left "remembered" by a user who left.
 */

export const REMEMBER_ME_KEY = 'aabar.rememberUntil';
export const REMEMBER_ME_DAYS = 30;

const WINDOW_MS = REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000;

function read(): string | null {
  try {
    return localStorage.getItem(REMEMBER_ME_KEY);
  } catch {
    return null;
  }
}

/** Record (or withdraw) the opt-in at sign-in time. */
export function setRememberMe(enabled: boolean, now: number = Date.now()): void {
  try {
    if (enabled) localStorage.setItem(REMEMBER_ME_KEY, String(now + WINDOW_MS));
    else localStorage.removeItem(REMEMBER_ME_KEY);
  } catch {
    // Storage unavailable (private mode, quota) — the session simply falls back to the idle lock.
  }
}

export function clearRememberMe(): void {
  setRememberMe(false);
}

/**
 * The epoch-ms instant the remembered window ends, or null when not remembered, already expired,
 * corrupt, or tampered beyond the allowed window (a hand-edited far-future value is not honoured).
 */
export function rememberedUntil(now: number = Date.now()): number | null {
  const raw = read();
  if (raw === null || raw === '') return null;
  const until = Number(raw);
  if (!Number.isFinite(until)) return null;
  if (until <= now || until > now + WINDOW_MS) return null;
  return until;
}

export function isRemembered(now: number = Date.now()): boolean {
  return rememberedUntil(now) !== null;
}
